/**
 * execution-contract — the SINGLE typed builder + parse guard for `CronExecution`.
 *
 * Red line #9 (单一真相源 · 反序列化防御): every `CronExecution` that crosses a
 * producer↔consumer boundary — desktop authoring (producer) ↔ server / dispatcher
 * firing (consumer), or a cron/task row round-tripping through SQLite/JSON — MUST
 * be constructed by `buildCronExecution` and re-hydrated by `parseCronExecution`.
 * No call site is allowed to hand-roll an object literal as its only path, and no
 * `JSON.parse` result is indexed before this module has guarded the parent object.
 *
 * `parseCronExecution` never lets a raw `Cannot read properties of undefined` escape:
 * a missing/mistyped field lands as a friendly `CronExecutionParseError` carrying an
 * agent/user-safe message (no internal host names, red line #9 退役名清退).
 *
 * This module is fs-free, network-free and unit-tested. The three execution tiers it
 * models (script / modelCall / agentTurn) are documented at the `CronExecution` union.
 */
import { ALL_MCP_SERVERS, isAllMcpServers } from "./mcp-plugin-name.js";
import { isTaskResultDelivery, } from "./types/index.js";
import { JUGUANG_MCP_SERVER_NAME } from "./enterprise-credentials.js";
import { DOSIA_PROJECTS_MCP_SERVER_NAME } from "./projects-mcp-contract.js";
import { invalidMaxBudgetUsdMessage, isValidMaxBudgetUsd } from "./max-budget-usd.js";
import { findSecretSpans } from "./secret-redaction.js";
import { parseFeishuTaskTrigger } from "./feishu-message-subscription.js";
import { messageSendTextsChanged, missingMessageRecipientsReason, readMessageRecipients } from "./message-recipients.js";
/**
 * Allowlist of environment-variable KEYS a `script` execution may declare inline.
 * Benign runtime knobs only — TZ / locale / python buffering. Credentials, tokens
 * and gateway keys travel the host injection channel (out-of-band), NEVER the job
 * definition, so this list deliberately excludes anything secret-shaped. Grow it
 * here (one place) if a new deterministic-script need appears.
 */
export const SCRIPT_ENV_ALLOWLIST = [
    "TZ",
    "LANG",
    "LC_ALL",
    "NODE_ENV",
    "PYTHONUNBUFFERED",
    "PYTHONIOENCODING",
];
/**
 * `CronExecution` 的全部 kind —— 导出是为了让**穷举型**的测试/消费方从它派生，而不是
 * 各自手抄一份数组：手抄的那份在 union 长出新 kind 时不会失败，只会静默漏过那一格。
 */
export const CRON_EXECUTION_KINDS = [
    "systemEvent",
    "agentTurn",
    "loopWakeup",
    "script",
    "modelCall",
];
// ── 设备腿执行形态声明（`EnterpriseTask.deviceExecution`）────────────────────
/**
 * `deviceExecution` 允许的 kind 子集。**常量而不是散落的 if**：创建面的校验、
 * 用户可见的错误文案、契约测试三处读同一份，加一个形态不会只改到一半。
 *
 * 为什么只有这两个：
 *  · `modelCall` —— 它会在**员工自己的电脑上**发起一次计费的模型调用。N-106 起设备腿
 *    `agentTurn` 认 `maxBudgetUsd`（员工电脑派发器把它交给会话装配），但 `modelCall` 这一档
 *    在派发器里没有执行腿，放开它等于收下一个员工电脑不会执行的形态。
 *  · `systemEvent` / `loopWakeup` —— 服务端/本机调度器的概念（往某个 chat 注入一条
 *    消息、唤醒一个自驱循环），派发器那条腿根本没有消费者，声明了也只会静默不生效。
 */
export const DEVICE_EXECUTION_KINDS = ["script", "agentTurn"];
/**
 * 该员工 L2 企业共享子目录（`<visibleRoot>/users/<userId>/personal/.dosia-org-shared`）
 * 的**物理根**，env 名。
 *
 * **host 在 spawn 那一刻注入，不是任务声明面**：绝不允许加进 {@link SCRIPT_ENV_ALLOWLIST}
 * ——同 `MCP_BRIDGE_TOKEN_ENV` 的理由（mcp-bridge-contract.ts）。管理员在云端写任务时
 * 根本不知道每台员工机的家目录长什么样，他能写出来的只会是猜的；由宿主用员工自己已
 * 解析好的 layers 现算，才是唯一不会指错的来源。
 */
export const ORG_SHARED_DIR_ENV = "DOSIA_ORG_SHARED_DIR";
/**
 * 随应用打包的任务脚本目录，env 名。同上：宿主注入，禁进 {@link SCRIPT_ENV_ALLOWLIST}。
 * 让脚本形态的任务命令能写成 `node "$DOSIA_TASK_SCRIPTS_DIR/x.mjs"` 而不是把某台机器
 * 的绝对路径焊进任务定义。
 */
export const TASK_SCRIPTS_DIR_ENV = "DOSIA_TASK_SCRIPTS_DIR";
/**
 * 当前员工在这台电脑上可知的飞书 open_id（本机绑定属于本人时它的全部 open_id，加组织
 * 通讯录里本人那一条），逗号分隔，env 名（N-159）。同上：宿主注入，禁进 {@link SCRIPT_ENV_ALLOWLIST}。
 *
 * 随包的取数脚本拿它核对「这台电脑上飞书登的是不是这个 DOSIA 账号的本人」：不在集合里就按
 * 未授权退出，不下载、不写文件——否则共用电脑或残留别人的登录时，别人的妙记会落进这个人的
 * 企业共享文件夹。一个都不知道时宿主不注入，脚本照常执行并在摘要里记「没核对」。
 */
export const FEISHU_EXPECTED_OPEN_IDS_ENV = "DOSIA_FEISHU_EXPECTED_OPEN_IDS";
/**
 * 员工电脑领「脚本形态」任务（`deviceExecution.kind === "script"`）的最低 DOSIA 版本（N-159）。
 *
 * 脚本形态任务跑的是**这台电脑随包带的**脚本，所以它对不对取决于电脑上的版本：2026-09-09 之前
 * 的客户端根本不认脚本形态（把任务说明当一句话交给 AI 跑，还记成功）；认得的客户端里，Windows
 * `$NAME` → `%NAME%` 改写进 0.4.14（#4220）、取数脚本按 lark-cli 真实回执判成功进 0.4.15
 * （#4238），更早的每轮必败。企业后端按请求头 `x-dosia-app-version` 判：没带头（0.4.11 之前）或
 * 低于本值的电脑不下发脚本任务，那一轮记 `client_outdated`。
 *
 * 随包脚本再有「旧版必败」的修复时，把本值提到那一版。
 */
export const DEVICE_SCRIPT_TASK_MIN_APP_VERSION = "0.4.15";
/**
 * 设备腿脚本约定的「员工本人的登录态不在」退出码。
 *
 * 为什么要给这一个失败单独定一个码：它是这条腿**最常见**的失败（飞书 refresh 窗口
 * 实测 7 天，任何休假超一周的员工回来都会撞上），而 `EnterpriseTaskOutcome` 里
 * 早就有 `not_logged_in` —— 那一档的注释写着管理员的下一步动作就是「找这位员工去
 * 登录一次」。不接这个码，最常见的失败会被压成最含糊的 `page_failed`（「页面/流程
 * 没走通」），管理员按类聚合时这个枚举就白开了。判定链：脚本 exit 3 →
 * `scriptSpawnEvidence` 把退出码作为**结构化事实**放进 `CronTurnEvidence.exitCode`
 * → `deviceScriptOutcome` 映射成 `not_logged_in`。与 `timedOut` 同性质、同理由：
 * **不许消费方去正则匹配 evidence.text 里那句中文文案**。
 *
 * 取 3 而不是 1/2：1 是「脚本自己崩了」的通用值，2 是 shell 的用法错误惯例，都可能
 * 由脚本以外的东西产生；3 起才是脚本可以自己定义语义的区间。
 *
 * ⚠️ 随包下发的任务脚本是独立 `.mjs`，运行期 import 不到 `@dosia/shared`，所以它那边
 * **必然**有一份字符串/数字副本。副本可以有、漂移不可以：
 * `apps/desktop/scripts/__tests__/daily-minutes-digest.test.mjs` 有一格从脚本模块取它
 * 导出的常量与本常量断言相等，改一边另一边即刻红。
 */
export const DEVICE_SCRIPT_NOT_AUTHORIZED_EXIT_CODE = 3;
/**
 * 设备腿脚本约定的「只完成了一部分」退出码（N-159）：大部分条目做成了、有几条没成。判定链与
 * {@link DEVICE_SCRIPT_NOT_AUTHORIZED_EXIT_CODE} 相同（退出码是结构化事实，`deviceScriptOutcome`
 * 映射成 `partial`），不去认 evidence.text 里的文案。
 *
 * 为什么单开一个码：妙记取数脚本原来名下只要有一场取不到（没有导出权限、转写还没生成）就整轮退 4，
 * 那一场在 14 天回看窗里每晚都再失败一次，这个人连续两周都是「页面没走通」，他其实交了的大部分没人看得见。
 *
 * 取 11：Node 自己用 1、3–7、9、10、12–14、124 与 128 以上（官方文档 process「Exit codes」，2026-11-02 读），shell 用 1、2、126、127，
 * 3 是上面的未登录、10 是 {@link CRON_PRECHECK_NO_WORK_EXIT_CODE}；11 没有人占，Node 崩溃不会碰巧报出它。
 * 随包脚本里的副本由 `apps/desktop/scripts/__tests__/daily-minutes-digest.test.mjs` 钉住相等。
 */
export const DEVICE_SCRIPT_PARTIAL_EXIT_CODE = 11;
/**
 * 设备腿脚本约定的「这台电脑上要用的连接器命令行工具不在」退出码（B-354）：脚本要调的 CLI（妙记取数脚本的
 * `lark-cli`）起不来（ENOENT）。判定链与 {@link DEVICE_SCRIPT_NOT_AUTHORIZED_EXIT_CODE} 相同：退出码是
 * 结构化事实，`deviceScriptOutcome` 映射成 `permission_denied`（「缺连接器/授权，本机做不了这件事」），
 * 与 agent 腿预检里「本机缺声明的连接器」同一个结论。
 *
 * 为什么单开一个码：此前它和「宿主没注入共享目录」共用退出码 2，派发器只能记最含糊的 `page_failed`，会议对账单
 * 不点名，管理员不知道该找谁；而它的下一步动作是员工本人在「设置 → 连接器」里重新启用一次。宿主在开跑前已等过
 * 插件集与连接器依赖就绪（core `prepareScriptSpawnEnv`），还撞上它说明这台电脑上的连接器确实没装好或被停用。
 *
 * 取 15：Node 自己用 1–10、12–14、124 与 128 以上（官方文档 process「Exit codes」，2026-11-04 读），shell 用 1、2、
 * 126、127，3 / 10 / 11 已是上面几个约定。随包脚本里的副本由 `apps/desktop/scripts/__tests__/daily-minutes-digest.test.mjs`
 * 钉住相等。
 */
export const DEVICE_SCRIPT_CONNECTOR_MISSING_EXIT_CODE = 15;
/**
 * 零模型前置检查（`agentTurn.precheck`，以及 `modelCall` 的预取命令）约定的「没有新活」退出码（B-124）。
 *
 * 判据只有退出码，不看输出：「打了日志但没活」与「脚本异常静默」都会让「空输出 = 没活」判错；
 * 退出码由吞退出码护栏（{@link assertExitCodePropagatingCommand}）保证能传出来。
 * 0 = 有活（照常唤醒 AI），本值 = 没活（这一轮不唤醒 AI、记 `skipped`），其它 = 失败。
 *
 * 取 10 而不是更小的值：1 / 2 / 126 / 127 / 130 被 shell 与常见工具占用，3 已是员工电脑腿的
 * 「未登录」（{@link DEVICE_SCRIPT_NOT_AUTHORIZED_EXIT_CODE}）。消费方：core 执行器
 * （`runCronPrecheck` / modelCall 预取），桌面调度器、云端 task-runner、员工电脑派发器都经它。
 */
export const CRON_PRECHECK_NO_WORK_EXIT_CODE = 10;
/**
 * 写成**类型谓词**而不是在 assert 里裸 `includes`：它同时替 TS 收窄 `execution` 本身，
 * 下面才能直接读 script/agentTurn 共有的桥字段，而不必再写一遍永不可达的 kind 判别
 * （那种「防御性」分支只是死代码，还会让人误以为这里真会收到别的 kind）。
 * 清单仍是 {@link DEVICE_EXECUTION_KINDS} 一份。
 */
function isDeviceExecution(execution) {
    return DEVICE_EXECUTION_KINDS.includes(execution.kind);
}
/**
 * 「这件事在哪儿跑」只能有一个答案 —— `execution`（服务器执行）与 `deviceExecution`
 * （派到员工电脑执行）**互斥**的唯一判据。
 *
 * 为什么必须是一个函数（红线 #9）：这条判据有两个入口，两个都是**创建面**——
 *   · HTTP 创建面 `parseCreateTaskPayload`（apps/web/server/enterprise/task-actions.ts）
 *   · 审批晋升面 `promoteTaskSubmission`（同目录 review-actions.ts）—— 「批准」这条路
 *     同样是**新建**一个线上任务，一份历史/伪造的载体不能靠它绕开创建面的门。
 * 两个入口都经 {@link buildNewEnterpriseTaskExecution}（新建任务的创建期规则清单）调到它。
 * 各写一遍 if 的后果不是「多写几行」，而是漏的那一边成为静默后门：两个字段同时带值时
 * 路由只看 `execution != null` 走服务器腿，`deviceExecution` 被静默忽略——管理员看到
 * 「创建成功」，员工电脑上那段脚本永远不跑。这正是这条腿最忌讳的失败形态。
 *
 * 参数收 `unknown`：它是**存在性**判据，不是形状判据（形状由 parse/build 负责），
 * 所以未解析的原始 body 与已解析的载体都能直接喂进来。
 */
export function assertExclusiveExecutionPlacement(input) {
    if (input.execution !== undefined && input.deviceExecution !== undefined) {
        throw new CronExecutionParseError("execution 与 deviceExecution 只能二选一：要在服务器上跑（关机也照跑）就只填 execution；" +
            "要派到员工自己的电脑上、以员工本人身份跑就只填 deviceExecution。" +
            "两个都填时无法确定这件事到底在哪儿执行。");
    }
}
/**
 * `resultDelivery`（云端任务跑完把结果送达本人，N-57 期 4 D4-02 F）的**创建面**判据单源：
 * 值必须在闭集里（`isTaskResultDelivery`），且只能与服务器腿（`execution`）同用——
 * 派到员工电脑的任务（无 `execution`）不走服务端 runner，结果送达钩子永远不会被触发，
 * 放行就是「建了、批准了、每周照跑、却没人收到」的静默失败。
 *
 * HTTP 创建面（`parseCreateTaskPayload`）与审批晋升面（`promoteTaskSubmission`）经
 * {@link buildNewEnterpriseTaskExecution} 调它，更新面（`updateTask` 以合并后的任务判）直接调它，
 * 三处不各写一遍 if。参数收 `unknown`：原始 body 与已解析载体都能直接喂进来。
 */
/** 闭集校验（值不在闭集 → `CronExecutionParseError`，创建 / 更新面映射成 400）。 */
export function parseTaskResultDelivery(value) {
    if (!isTaskResultDelivery(value)) {
        throw new CronExecutionParseError(`resultDelivery 只接受 "monitor-weekly-report"（每周盯盘周报送达本人），收到的是 ${JSON.stringify(value)}。`);
    }
    return value;
}
/**
 * 「这条任务由事件触发、不按时间表跑」的**唯一判定**（N-57 期 4 D4-03）。
 *
 * 消费方：task-runner `tickOrg`（跳过，不按 `trigger` 触发）、task-actions 的页面脸读写面（任务列表 /
 * 单个任务 / 执行记录 / 改删立即执行一律排除，员工任务页与管理后台都经它）、task-store 能力定义读面（排除）。
 * 对话脸（`cloud_task_manage`）只排除 {@link isSystemManagedEventTask}。
 * 判据刻意宽：`eventSource` 只要**存在**（哪怕形状坏了）就算事件任务——宁可一条坏载体不被
 * 时间表触发，也不让它以一个永不到期的占位 `trigger` 以外的任何方式混进定时面。
 */
export function isEventSourcedTask(task) {
    return task?.eventSource !== undefined && task.eventSource !== null;
}
/**
 * 事件任务的永不到期占位排期（`at` 档）。事件任务不按时间表触发，`trigger` 只为满足任务形状；
 * 盯盘巡检载体与飞书消息触发的任务都写这一个值。
 */
export const EVENT_TASK_NEVER_DUE_AT = "9999-12-31T00:00:00.000Z";
/** 排期是不是事件任务的占位（审批晋升面据此核载体没被改过）。 */
export function isEventTaskPlaceholderTrigger(trigger) {
    if (!trigger || typeof trigger !== "object")
        return false;
    const t = trigger;
    return t.kind === "at" && t.at === EVENT_TASK_NEVER_DUE_AT;
}
/**
 * 系统托管的事件任务（盯盘巡检载体）：任何读写面都看不到它（含对话脸）。判据宽同
 * {@link isEventSourcedTask}：有 `eventSource` 但不是飞书消息触发的，一律当系统托管。
 */
export function isSystemManagedEventTask(task) {
    return isEventSourcedTask(task) && !isFeishuTriggeredTask(task);
}
/**
 * 「飞书群来新消息时跑」的任务（N-163 P3）：员工 / 管理员经对话建，对话脸可见可管，页面脸看不到
 * （桌面任务页与管理后台按排期显示任务，说不清「群里来消息时跑」，维护者 D-7 不做界面）。
 */
export function isFeishuTriggeredTask(task) {
    const source = task?.eventSource;
    return !!source && typeof source === "object" && source.kind === "feishu-message";
}
/** 执行对象恰好只有这位员工（`{kind:"users", userIds:[本人]}`，重复的 id 不算第二个人）。读不懂的形状算「不是」。 */
export function targetsOnlyUser(target, user) {
    if (!target || typeof target !== "object")
        return false;
    const { kind, userIds } = target;
    if (kind !== "users" || !Array.isArray(userIds))
        return false;
    const ids = new Set(userIds);
    return ids.size === 1 && ids.has(user);
}
/**
 * 「本人的任务」的**唯一判定**（N-182 P4）：本人建的，且只派给本人。
 *
 * 消费方：task-actions 的控制门（员工能不能运行、暂停、恢复、下线）、手机范围门、桌面任务卡和详情的按钮。
 * 只看建任务的人不够：管理员在桌面建的全员任务也是他建的，手机上误点一下就对全员生效；员工的提案批准后也可能
 * 派给多人，动它会影响别人。身份为空一律不算，空串不能去匹配缺字段的存量行。
 */
export function isOwnEnterpriseTask(task, user) {
    return user !== "" && task.createdBy === user && targetsOnlyUser(task.target, user);
}
/**
 * 员工能不能运行、暂停、恢复、下线这条任务（N-182 P4，维护者 2026-11-01 拍板「管理员暂停的员工不能恢复」）：
 * 本人的，并且没停，或者是本人停的。没有 `pausedBy` 的（老任务、连续失败自动暂停）按管理员暂停算。B-335 的系统暂停（连续没有新结果、
 * 太密又没有前置检查）`pausedBy` 写建任务的人：不是坏了要人修，本人决定还要不要它。
 *
 * 读单条不看这个：本人的任务停着也能看。
 */
export function ownerMayOperateEnterpriseTask(task, user) {
    return isOwnEnterpriseTask(task, user) && (task.status === "active" || task.pausedBy === user);
}
/** 存量行再水化用的守卫：形状不符 → `null`（读取面据此判坏行）。 */
export function parseEnterpriseTaskEventSource(raw) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
        return null;
    const r = raw;
    if (r.kind === "feishu-message")
        return { kind: "feishu-message" };
    if (r.kind !== "juguang-monitor-inspection")
        return null;
    if (typeof r.ruleId !== "string" || r.ruleId.length === 0)
        return null;
    return { kind: "juguang-monitor-inspection", ruleId: r.ruleId };
}
/**
 * 「飞书群来新消息时跑」声明（`feishuTrigger`）的创建期规则（N-163 P3），挂在
 * {@link buildNewEnterpriseTaskExecution} 里——创建面与审批晋升面同一份。带声明时：
 *  - 必须在服务器上用完整 AI 跑（`execution.kind === "agentTurn"`）：群消息经附加提示词交给它，
 *    脚本 / 取数成文接不到；
 *  - 不得派到员工电脑（`deviceExecution` 是 P2 那条腿）；
 *  - 不得带 `resultDelivery`（结果固定经飞书消息触发自己的通道送订阅人本人）。
 * 「不得给 trigger」「执行对象只能是本人」判的是原始载荷与操作人，由各写入口调
 * {@link feishuTriggerTargetRejection} 与自己的 trigger 判断。返回解析后的声明。
 */
export function assertFeishuTriggerPlacement(input) {
    if (input.feishuTrigger === undefined)
        return undefined;
    const parsed = parseFeishuTaskTrigger(input.feishuTrigger);
    if (!parsed.ok)
        throw new CronExecutionParseError(parsed.error);
    if (input.deviceExecution !== undefined) {
        throw new CronExecutionParseError("飞书消息触发的任务只在服务器上跑，不能填 deviceExecution。");
    }
    if (input.resultDelivery !== undefined) {
        throw new CronExecutionParseError("飞书消息触发的任务结果固定发给你本人，不要填 resultDelivery。");
    }
    if (input.execution?.kind !== "agentTurn") {
        throw new CronExecutionParseError("飞书消息触发的任务要在服务器上用完整 AI 处理群消息：execution 必须是 agentTurn。");
    }
    return parsed.value;
}
/**
 * 飞书消息触发的任务只替建任务的人本人跑（订阅人 = 执行人）：`target` 必须恰为
 * `{kind:"users", userIds:[ownerUserId]}`。不合 → 中文拒绝句；合 → `null`。
 */
export function feishuTriggerTargetRejection(target, ownerUserId) {
    const t = target;
    const ok = t?.kind === "users"
        && Array.isArray(t.userIds)
        && t.userIds.length === 1
        && t.userIds[0] === ownerUserId;
    return ok ? null : "飞书消息触发的任务只能替建任务的人本人跑，target 只能是你自己。";
}
/**
 * 创建 / 更新 / 审批晋升载荷的拒收门：`eventSource` 只能由企业后端自己建（盯盘巡检载体；飞书消息触发
 * 由 `feishuTrigger` 声明换算），管理员、员工提案、`cloud_task_manage` 都不能带。带了 → `CronExecutionParseError`（映射 400）。
 * 不「丢掉就好」：丢掉 = 调用方以为建了一条事件任务、实际建出一条按占位时间表的普通任务。
 */
export function assertNoEventSourceInPayload(input) {
    if (input.eventSource !== undefined) {
        throw new CronExecutionParseError("eventSource 由系统设置，创建或修改任务时不能填写。");
    }
}
export function assertResultDeliveryPlacement(input) {
    if (input.resultDelivery === undefined)
        return;
    parseTaskResultDelivery(input.resultDelivery);
    if (input.execution === undefined || input.execution === null) {
        throw new CronExecutionParseError("resultDelivery 只能用在服务器上跑的任务（填了 execution）上；派到员工电脑的任务跑完不会经服务器送达结果。");
    }
}
/**
 * 设备腿（派到员工电脑执行）执行声明的**创建面**校验。
 *
 * 构造/解析仍然走 {@link buildCronExecution} / {@link parseCronExecution}——本函数只加
 * 「设备腿额外不许有什么」这一层，不复制任何字段守卫（红线 #9）。放在创建面而不是解析
 * 面：存量行必须原样再水化，读取面永远宽于创建面（同 `assertBridgeConnectorDeclaration`
 * 的姿态）。
 *
 * 三条拒绝各自的**理由**（删之前请先证伪这三条，别只看结论）：
 *
 * ① **kind 子集** —— 见 {@link DEVICE_EXECUTION_KINDS} 的注释。
 *
 * ② **禁调用桥（mcpServers / connectorPolicy）** —— 派发器
 *    `apps/desktop/src/main/enterprise-task-dispatcher.ts` 的 `queueTail`「同机全部
 *    串行」判据链，原文是：legacy-device ⇒ 读不到 `execution.mcpServers` ⇒ 拿不到声明面
 *    ⇒ 没有可靠判据 ⇒ 只能全串行。`deviceExecution` 一旦能带 mcpServers，这个前提当场
 *    被证伪，而串行判据不会报错、只会静默失效（两件都要开浏览器的任务又开始抢同一个
 *    Chrome）。禁掉它，是让那条已经写下来的推理继续成立。
 *    **连 `[]` 也拒**：派发器读 `deviceExecution.kind` 与 `agentTurn` 的 `agent`（B-127）、
 *    `model` / `framework` / `timeoutSeconds` / `dosiaTools` / `maxBudgetUsd` / `projectSlug`
 *    （N-106），从不消费连接器声明面——收下一个永远不会被消费的声明，是在契约上撒一个
 *    没人会发现的谎。
 *
 * ③ **禁 cwd** —— `collectExecutionPathTokens`（本文件）把 `cwd` 当**整条路径候选**
 *    送进路径守卫；但管理员在云端不知道每台员工机的家目录，他写得出的 cwd 只能是猜的。
 *    工作目录由宿主用 {@link ORG_SHARED_DIR_ENV} / {@link TASK_SCRIPTS_DIR_ENV} 注入。
 */
export function assertDeviceExecution(execution) {
    if (!isDeviceExecution(execution)) {
        throw new CronExecutionParseError(`派到员工电脑执行的任务只支持「${DEVICE_EXECUTION_KINDS.join(" / ")}」两种形态，` +
            `收到的是「${execution.kind}」。` +
            "要在服务器上跑取数成文（modelCall）请改填 execution 而不是 deviceExecution；" +
            "systemEvent / loopWakeup 是调度器内部形态，员工电脑这条腿不执行它们。");
    }
    // 上一格已经把 kind 收窄到 script / agentTurn —— 两者都带这组桥字段，直接解构。
    const { mcpServers, connectorPolicy } = execution;
    if (mcpServers !== undefined) {
        throw new CronExecutionParseError("派到员工电脑执行的任务不能声明 mcpServers（连接器）。" +
            "员工电脑上同一时刻只跑一件事，靠的正是「这条腿没有连接器声明面」这个前提；" +
            "要用连接器请改成服务器执行（填 execution），或者在任务描述里说清要做什么、" +
            "由员工电脑上已授权的默认工具面去做。");
    }
    if (connectorPolicy !== undefined) {
        throw new CronExecutionParseError("派到员工电脑执行的任务不能声明 connectorPolicy。" +
            "它回答的是「连接器挂不上时失败还是照跑」，而这条腿不接受连接器声明（见上一条），" +
            "留着它只会让人以为设置生效了。请删掉这个字段。");
    }
    // 前置检查（B-124）在员工电脑上跑，与脚本档同一个理由不许写 cwd。
    const cwd = execution.kind === "script" ? execution.cwd : execution.precheck?.cwd;
    if (cwd !== undefined) {
        throw new CronExecutionParseError(`派到员工电脑执行的${execution.kind === "script" ? "脚本" : "前置检查"}不能写 cwd（工作目录）。` +
            "每台员工电脑的目录位置都不一样，在云端填的路径必然是猜的；" +
            `脚本要用的两个目录由员工电脑在开跑那一刻注入环境变量：` +
            `${ORG_SHARED_DIR_ENV}（本人的企业共享文件夹）、${TASK_SCRIPTS_DIR_ENV}（随应用下发的脚本目录），` +
            "命令里直接引用这两个变量即可。");
    }
}
/**
 * 派到员工电脑的 `agentTurn` 时限上限（秒，N-106 维护者 2026-10-08 拍板 50 分钟）。
 *
 * 理由：员工电脑开跑后，企业后端在 1 小时（`DEVICE_ROUND_DEADLINE_MS`，
 * apps/web/server/enterprise/device-task-round-digest.ts）内收不到结果就把这次执行标成「超时未报」、
 * 对账单记「电脑没在线」。时限 = 检测窗口减去收尾余量（开跑回报到计时开始的预检、超时后停会话与
 * 上传执行日志），余量 10 分钟是假设值、未测量。服务器腿的 `timeoutSeconds` 不受此限。
 */
export const DEVICE_AGENT_TURN_MAX_TIMEOUT_SECONDS = 3000;
/**
 * 「pi 引擎不支持单次花费上限」的拒绝文案——云端建任务门、云端派发线（agent-host-contract）之外，
 * 员工电脑腿建任务 / 批准也用这一句（N-106 维护者拍板「与云端同一句」）。pi 引擎只有 token 预算闸，
 * 没有按美元计的熔断（packages/core/src/agent/backend/pi/loop.ts）。
 */
export const PI_MAX_BUDGET_UNSUPPORTED_MESSAGE = 'Pi 云端任务不支持 maxBudgetUsd 单次 USD 熔断；请移除该字段或改用 framework:"claude"';
/**
 * 云端任务里 Agent 与模型二选一（N-182 D10，P1 R4.1）：指名了 Agent 就由 Agent 自己决定模型。
 * 只在写入口判（{@link assertServerAgentTurnExecution}）；开跑面不判，存量两者同在的任务照跑（agent-host 忽略任务上的模型）。
 */
export const AGENT_WITH_MODEL_REJECTED_MESSAGE = "指名了 Agent 的云端任务不能再指定模型：模型由这个 Agent 自己决定。要用指定的模型跑，就去掉 Agent。";
/**
 * 工作台 Agent 锁定的模型在清单里有行、却两条引擎腿都没有（N-182 P1 R3.3）。对话里建任务补引擎时拒收、
 * agent-host 开跑时以配置失败结束，两处同一句。
 */
export function agentLockedModelUnreachableMessage(model) {
    return `这个 Agent 锁定的模型「${model}」在云端没有可用的接入，请联系管理员`;
}
/**
 * 派到员工电脑的 `agentTurn` 建任务 / 批准晋升时的约束（N-106）。员工电脑会照着任务里写的
 * `timeoutSeconds` / `maxBudgetUsd` / `framework` 跑，所以这几项在写入时就要能跑得通：
 *  · `timeoutSeconds` 必须 > 0 且不超过 {@link DEVICE_AGENT_TURN_MAX_TIMEOUT_SECONDS}；
 *  · `framework: "pi"` 不能带 `maxBudgetUsd`（与云端 {@link assertServerAgentTurnExecution} 同一句）。
 *
 * **只在写入面跑，不进 {@link assertDeviceExecution}**：后者员工电脑开跑前也会重跑，放进去会让
 * 已经建好的存量任务突然停跑（主会话裁决 T7 取保守档；存量统计回来后再议是否收紧）。存量里
 * pi + 花费上限的行由员工电脑在运行时这一次失败并说明。
 * `script` 形态与非设备腿一律 no-op。
 */
export function assertDeviceAgentTurnAuthoring(execution) {
    if (!execution || execution.kind !== "agentTurn")
        return;
    const { timeoutSeconds } = execution;
    if (timeoutSeconds !== undefined && (timeoutSeconds <= 0 || timeoutSeconds > DEVICE_AGENT_TURN_MAX_TIMEOUT_SECONDS)) {
        throw new CronExecutionParseError(`派到员工电脑的任务时限（timeoutSeconds）必须大于 0 且不超过 ${DEVICE_AGENT_TURN_MAX_TIMEOUT_SECONDS} 秒` +
            `（最长 ${DEVICE_AGENT_TURN_MAX_TIMEOUT_SECONDS / 60} 分钟），收到的是 ${timeoutSeconds}。` +
            "员工电脑开始执行后，服务端 1 小时内收不到结果就会记为「电脑没在线」；" +
            "更长的活请拆成几条，或改在服务器上跑（填 execution）。");
    }
    if (execution.framework === "pi" && execution.maxBudgetUsd !== undefined) {
        throw new CronExecutionParseError(PI_MAX_BUDGET_UNSUPPORTED_MESSAGE);
    }
}
/** Friendly, non-raw error for any malformed `CronExecution` payload. */
export class CronExecutionParseError extends Error {
    constructor(message) {
        super(message);
        this.name = "CronExecutionParseError";
    }
}
// ── path-boundary helpers (exported so desktop migration reuses ONE implementation) ──
/**
 * Normalize a path for lexical containment checks:
 *  - `\` → `/`
 *  - Windows drive letter upper-cased
 *  - collapse repeated `/`
 *  - strip trailing `/` (except root `/` and `C:/`)
 * Pure string — no fs, no path.resolve (that would bake in host cwd).
 */
export function normalizePathForCompare(p) {
    let n = p.replace(/\\/g, "/");
    if (/^[A-Za-z]:/.test(n)) {
        n = n[0].toUpperCase() + n.slice(1);
    }
    // Keep leading `//` (UNC) as two slashes; collapse the rest.
    if (n.startsWith("//")) {
        n = "//" + n.slice(2).replace(/\/+/g, "/");
    }
    else {
        n = n.replace(/\/+/g, "/");
    }
    if (n.length > 1 && n.endsWith("/")) {
        // Keep `C:/` as drive root (length 3 after strip would become `C:` which is wrong).
        if (!(n.length === 3 && /^[A-Z]:\/$/.test(n))) {
            n = n.slice(0, -1);
        }
    }
    return n;
}
/**
 * True iff `candidate` equals `root` or is a descendant, with path-segment
 * boundary (`/a/b` does NOT match `/a/bc`). Both sides are normalized first.
 */
export function isPathUnderRoot(candidate, root) {
    const c = normalizePathForCompare(candidate);
    const r = normalizePathForCompare(root);
    if (c === r)
        return true;
    const prefix = r.endsWith("/") ? r : r + "/";
    return c.startsWith(prefix);
}
/**
 * Single authority for shell absolute-path tokenization. Keeps value + span +
 * quote so rewrites can splice the original command once (no iterative
 * substring replace that corrupts prefix-sibling tokens).
 *
 * Does not expand env vars or resolve relative paths — those stay
 * host-cwd-relative at fire time and are out of scope for this gate.
 * Flag-shaped fragments like `--flag=/abs/x` are a single bare token and are
 * not treated as absolute paths.
 */
export function extractAbsolutePathTokenSpans(command) {
    if (typeof command !== "string" || command.length === 0)
        return [];
    const spans = [];
    // Match either a double-quoted, single-quoted, or bare token.
    // ONE regex — extractAbsolutePathTokens derives from these spans.
    const re = /"([^"]*)"|'([^']*)'|(\S+)/g;
    let m;
    while ((m = re.exec(command)) !== null) {
        let value;
        let quote;
        // Distinguish capture groups carefully: empty quoted `""` yields "" in
        // group 1 (not undefined), so check which group matched via !== undefined.
        if (m[1] !== undefined) {
            value = m[1];
            quote = "double";
        }
        else if (m[2] !== undefined) {
            value = m[2];
            quote = "single";
        }
        else {
            value = m[3] ?? "";
            quote = "none";
        }
        if (!value)
            continue;
        if (!isAbsolutePathToken(value))
            continue;
        spans.push({
            value,
            start: m.index,
            end: m.index + m[0].length,
            quote,
        });
    }
    return spans;
}
/**
 * Heuristic: split a free-form shell command on whitespace / quotes and return
 * every token that looks like an absolute path (Unix `/…` or Windows `X:\…` /
 * `X:/…`). Derived from {@link extractAbsolutePathTokenSpans} — do not copy the
 * tokenizer regex elsewhere.
 */
export function extractAbsolutePathTokens(command) {
    return extractAbsolutePathTokenSpans(command).map((s) => s.value);
}
function isAbsolutePathToken(token) {
    // Unix absolute (not `//` comment-style alone; single leading `/` is enough).
    if (token.startsWith("/") && token.length > 1)
        return true;
    // Windows drive path: `C:\…` or `C:/…`
    if (/^[A-Za-z]:[\\/]/.test(token))
        return true;
    return false;
}
function isWindowsDrivePath(token) {
    return /^[A-Za-z]:[\\/]/.test(token);
}
/**
 * Conservative bare-token character set for POSIX sh paths executed via
 * `spawnCollect(..., { shell: true })`. Only characters that cannot change
 * shell structure or expand under bare evaluation. Anything outside this set
 * (`;`, `$`, `` ` ``, whitespace, quotes, redirects, globs, …) must be quoted
 * or rejected — never returned bare.
 *
 * Allowed: ASCII letters/digits, `/`, `.`, `_`, `-`, `+`, `@`, `%`, `=`, `~`.
 */
const POSIX_SAFE_BARE_RE = /^[A-Za-z0-9/._+@%=~-]+$/;
/**
 * True when `pathValue` is literal inside POSIX double quotes (no `$`, `` ` ``,
 * `\`, `"`, or line terminators — those remain active under `"`).
 */
function isSafeInPosixDoubleQuotes(pathValue) {
    return !/[\$`"\\\n\r]/.test(pathValue) && !pathValue.includes('"');
}
/**
 * True when `pathValue` may appear as an unquoted POSIX shell word without
 * changing command structure or expanding.
 */
function isSafeBarePosixPath(pathValue) {
    return POSIX_SAFE_BARE_RE.test(pathValue);
}
/**
 * cmd.exe-dangerous characters for path values fired through `shell: true` on
 * Windows. `%` expands even inside double quotes; `!` expands with delayed
 * expansion; `& | < > ^` and parentheses alter command structure. Explicit
 * rewrite failure is preferred over emitting a token the helper cannot prove
 * stays literal. Normal drive paths and spaces remain accepted.
 */
const WINDOWS_UNSAFE_PATH_RE = /[%!^&|<>()\n\r]/;
function isSafeWindowsDrivePathContent(pathValue) {
    if (WINDOWS_UNSAFE_PATH_RE.test(pathValue))
        return false;
    // Embedded double-quotes cannot be expressed safely by this helper.
    if (pathValue.includes('"'))
        return false;
    return true;
}
/**
 * Render a path value as a single shell token our tokenizer can round-trip.
 *
 * Scope: safe rendering of mapped absolute paths only — not a general shell
 * parser. Prefer the original quote style when that style keeps the value
 * literal under `shell: true`; otherwise use a safe alternative (POSIX single
 * quotes when the value has no `'`) or return `null`.
 *
 * POSIX:
 *  - bare only for {@link POSIX_SAFE_BARE_RE} (no `;`, `$`, metachar, …)
 *  - double quotes only when `$` / `` ` `` / `\` / `"` are absent
 *  - single quotes keep the path literal when no `'` is present
 *
 * Windows drive paths: never use POSIX-only single quotes; bare or double-quote
 * when content is cmd-safe; reject `%` / `!` / redirects / etc.
 */
export function renderAbsolutePathShellToken(pathValue, originalQuote = "none") {
    if (typeof pathValue !== "string" || pathValue.length === 0)
        return null;
    if (isWindowsDrivePath(pathValue)) {
        return renderWindowsDrivePathShellToken(pathValue, originalQuote);
    }
    return renderPosixPathShellToken(pathValue, originalQuote);
}
function renderPosixPathShellToken(pathValue, originalQuote) {
    const canBare = isSafeBarePosixPath(pathValue);
    const canDouble = isSafeInPosixDoubleQuotes(pathValue);
    const canSingle = !pathValue.includes("'");
    // Preserve original quote style only when that style keeps the value literal.
    if (originalQuote === "double" && canDouble)
        return `"${pathValue}"`;
    if (originalQuote === "single" && canSingle)
        return `'${pathValue}'`;
    if (originalQuote === "none" && canBare)
        return pathValue;
    // Safe alternatives when the original style is unsafe or insufficient:
    // 1. double — keeps spaces / `;` / most metachar literal and is cross-platform
    // 2. single — full literal when double would still expand (`$`, backticks, `\`)
    // 3. bare — only the conservative safe charset
    if (canDouble)
        return `"${pathValue}"`;
    if (canSingle)
        return `'${pathValue}'`;
    if (canBare)
        return pathValue;
    return null;
}
function renderWindowsDrivePathShellToken(pathValue, originalQuote) {
    if (!isSafeWindowsDrivePathContent(pathValue))
        return null;
    const needsQuote = /\s/.test(pathValue);
    // cmd.exe treats single quotes as ordinary characters — never use them as
    // a quoting mechanism for drive paths. Prefer double quotes when the
    // original token was double-quoted, had single quotes (abandon POSIX style),
    // or the path has whitespace.
    if (originalQuote === "double" || originalQuote === "single" || needsQuote) {
        return `"${pathValue}"`;
    }
    // original `none` with no spaces → bare (cmd-safe content only).
    return pathValue;
}
/**
 * Rewrite absolute-path tokens in `command` by splicing original spans once.
 * Only tokens whose unquoted `value` is a key in `valueMap` are replaced;
 * unrecognized fragments (e.g. `--flag=/abs/x`) are left untouched.
 */
export function rewriteCommandAbsolutePathTokens(command, valueMap) {
    if (typeof command !== "string") {
        return { ok: false, reason: "command is not a string" };
    }
    if (valueMap.size === 0)
        return { ok: true, command };
    const spans = extractAbsolutePathTokenSpans(command);
    let out = "";
    let cursor = 0;
    for (const span of spans) {
        const mapped = valueMap.get(span.value);
        if (mapped === undefined)
            continue;
        const rendered = renderAbsolutePathShellToken(mapped, span.quote);
        if (rendered === null) {
            return {
                ok: false,
                reason: `cannot safely quote rewritten path: ${mapped}`,
            };
        }
        out += command.slice(cursor, span.start);
        out += rendered;
        cursor = span.end;
    }
    out += command.slice(cursor);
    return { ok: true, command: out };
}
/**
 * Collect absolute path tokens that fail the allowedRoots / excludedRoots gate.
 * Empty when opts.allowedRoots is absent/empty (no validation).
 * Used by both the authoring gate and the one-shot L2 migration.
 */
export function findOutOfTreePathTokens(paths, opts) {
    const roots = opts.allowedRoots;
    if (!roots || roots.length === 0)
        return [];
    const excluded = opts.excludedRoots ?? [];
    const bad = [];
    for (const p of paths) {
        if (typeof p !== "string" || p.length === 0)
            continue;
        const underAllowed = roots.some((r) => isPathUnderRoot(p, r));
        const underExcluded = excluded.some((r) => isPathUnderRoot(p, r));
        if (!underAllowed || underExcluded)
            bad.push(p);
    }
    return bad;
}
/**
 * 系统目录（需求 13，wangkai 2026-10-20）：装系统 / 包管理器自带程序的目录。命令首词是这些目录里的
 * 解释器时，它是「用什么跑」而不是「跑哪个脚本」——不属于任务自己的文件，不进路径边界、也不进 L2 迁移。
 */
const SYSTEM_INTERPRETER_DIRS = [
    "/bin",
    "/usr/bin",
    "/usr/local/bin",
    "/opt/homebrew/bin",
    "/opt/local/bin",
];
/** 解释器 / 启动器的文件名（`python3.12`、`perl5.34` 这类带版本号的也算）。 */
const INTERPRETER_NAME_RE = /^(?:python(?:\d+(?:\.\d+)*)?|node|nodejs|bash|sh|zsh|dash|ksh|fish|ruby|perl(?:\d+(?:\.\d+)*)?|php|osascript|deno|bun|env|Rscript)$/;
/** `p` 是不是系统目录下的解释器（目录必须恰好是 {@link SYSTEM_INTERPRETER_DIRS} 之一，不含子目录）。 */
function isSystemInterpreterPath(p) {
    const n = normalizePathForCompare(p);
    const slash = n.lastIndexOf("/");
    if (slash <= 0)
        return false;
    return SYSTEM_INTERPRETER_DIRS.includes(n.slice(0, slash)) && INTERPRETER_NAME_RE.test(n.slice(slash + 1));
}
/**
 * 一条命令里要过路径边界的绝对路径 token：命令**第一个词**是系统目录下的解释器
 * （`/usr/bin/python3 tasks/x/run.py`）时跳过它本身，只留其后的脚本路径；解释器出现在别处
 * （`cd … && /usr/bin/python3 …`）不跳——判据只认首词，不做 shell 解析。
 */
function commandPathTokens(command) {
    const spans = extractAbsolutePathTokenSpans(command);
    const first = spans[0];
    const skipFirst = first !== undefined && command.slice(0, first.start).trim() === "" && isSystemInterpreterPath(first.value);
    return (skipFirst ? spans.slice(1) : spans).map((s) => s.value);
}
/** 命令 + 可选 cwd 的路径候选（cwd 整条当一个候选，不分词）。 */
function commandAndCwdPathTokens(command, cwd) {
    const out = [];
    if (typeof cwd === "string" && cwd.length > 0)
        out.push(cwd);
    out.push(...commandPathTokens(command));
    return out;
}
/**
 * Absolute paths referenced by an execution's own commands: script (cwd + command),
 * modelCall (pre-fetch command), agentTurn (前置检查 cwd + command，B-124). Empty for
 * other kinds. A system-dir interpreter as the command's first word is skipped
 * (需求 13) — one predicate for the authoring gate and the L2 migration.
 */
export function collectExecutionPathTokens(execution) {
    if (execution.kind === "script") {
        return commandAndCwdPathTokens(execution.command, execution.cwd);
    }
    if (execution.kind === "modelCall" && typeof execution.command === "string") {
        return commandPathTokens(execution.command);
    }
    if (execution.kind === "agentTurn" && execution.precheck) {
        return commandAndCwdPathTokens(execution.precheck.command, execution.precheck.cwd);
    }
    return [];
}
function assertExecutionPathsInRoots(execution, opts) {
    if (!opts?.allowedRoots || opts.allowedRoots.length === 0)
        return;
    const bad = findOutOfTreePathTokens(collectExecutionPathTokens(execution), opts);
    if (bad.length === 0)
        return;
    const recommended = opts.recommendedScriptDir?.trim() || "个人目录/tasks/";
    throw new CronExecutionParseError(`任务脚本必须放在受管目录内（推荐写入 ${recommended}），检测到树外路径：${bad[0]}`);
}
/**
 * 修改一条任务时，任务名 / 指令这类文字字段「给没给」的唯一判法：去掉首尾空白后为空 = 没给（返回
 * `undefined`），否则返回去掉首尾空白后的值。
 *
 * 模型改任务时常把不打算改的字段原样填成空串传回来；按「给了」处理就会把原任务名 / 指令写成空白，
 * 任务照常排期却在跑空指令。创建面一直按同一个判法要求必填（`parseCreateTaskPayload`），修改面
 * 与它对齐：对话工具面（中继 `cloud_task_manage`、本机 `cron_manage`）把空白当没给，HTTP 修改面
 * （`parseUpdateTaskPayload`）收到空白直接 400。
 */
export function providedTaskText(value) {
    if (typeof value !== "string")
        return undefined;
    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
}
// ── internal field guards (all defensive: parent object already null-checked) ──
function requireString(value, field, kind) {
    if (typeof value !== "string" || value.length === 0) {
        throw new CronExecutionParseError(`执行契约字段 "${field}" 缺失或类型错误（${kind} 需要非空字符串）`);
    }
    return value;
}
function optionalString(value, field, kind) {
    if (value === undefined)
        return undefined;
    if (typeof value !== "string") {
        throw new CronExecutionParseError(`执行契约字段 "${field}" 类型错误（${kind} 的 ${field} 须为字符串）`);
    }
    return value;
}
function optionalAgentFramework(value) {
    if (value === undefined)
        return undefined;
    if (value !== "claude" && value !== "pi") {
        throw new CronExecutionParseError('执行契约字段 "framework" 类型错误（agentTurn 仅支持 "claude" / "pi"，不支持 auto 或跨框架回退）');
    }
    return value;
}
function optionalAgentPolicy(value) {
    if (value === undefined)
        return undefined;
    if (value !== "prefer" && value !== "required") {
        throw new CronExecutionParseError('执行契约字段 "agentPolicy" 类型错误（agentTurn 仅支持 "required" / "prefer"）');
    }
    return value;
}
function optionalConnectorPolicy(value, kind) {
    if (value === undefined)
        return undefined;
    if (value !== "optional" && value !== "required") {
        throw new CronExecutionParseError(`执行契约字段 "connectorPolicy" 类型错误（${kind} 仅支持 "optional" / "required"）`);
    }
    return value;
}
function optionalNumber(value, field, kind) {
    if (value === undefined)
        return undefined;
    if (typeof value !== "number" || !Number.isFinite(value)) {
        throw new CronExecutionParseError(`执行契约字段 "${field}" 类型错误（${kind} 的 ${field} 须为有限数字）`);
    }
    return value;
}
function optionalStringArray(value, field, kind) {
    if (value === undefined)
        return undefined;
    if (!Array.isArray(value) || value.some((v) => typeof v !== "string")) {
        throw new CronExecutionParseError(`执行契约字段 "${field}" 类型错误（${kind} 的 ${field} 须为字符串数组）`);
    }
    return value;
}
/**
 * Guard the connector-surface axis: an explicit `string[]` subset, or the
 * {@link ALL_MCP_SERVERS} 显式全量 sentinel. `undefined` passes through (the
 * desktop path's legacy whole surface; the SERVER create gate is what rejects it —
 * see `assertServerAgentTurnExecution`).
 *
 * Deliberately NOT `optionalStringArray`: the sentinel is a STRING where a caller
 * might expect an array, so every deserialization boundary must be told about it
 * in one place (红线 #9). A bare `"foo"` is still rejected — only the one blessed
 * sentinel value is a legal non-array.
 */
function optionalMcpServerScope(value, field, kind) {
    if (value === undefined)
        return undefined;
    if (isAllMcpServers(value))
        return ALL_MCP_SERVERS;
    if (!Array.isArray(value) || value.some((v) => typeof v !== "string")) {
        throw new CronExecutionParseError(`执行契约字段 "${field}" 类型错误（${kind} 的 ${field} 须为字符串数组，或 "${ALL_MCP_SERVERS}" 表示全部已授权连接器）`);
    }
    return value;
}
/**
 * Guard the script/modelCall **bridge** connector declaration (A2′ loopback call
 * bridge, `mcp-bridge-contract.ts`).
 *
 * Deliberately NOT {@link optionalMcpServerScope}: the `"all"` sentinel is legal
 * for an `agentTurn` (a model picks from the mounted surface, and 全量 has to be
 * *sayable* so 遗漏 stays rejectable — see `assertServerAgentTurnExecution`), but
 * a script is a fixed program whose author knows exactly which connectors it
 * calls. Accepting `"all"` there would hand the bridge an allowlist far wider
 * than the script's actual call sites, for zero authoring benefit — so it is a
 * PARSE error, with a message that says what to write instead.
 */
function optionalBridgeMcpServers(value, kind) {
    if (value === undefined)
        return undefined;
    if (isAllMcpServers(value)) {
        throw new CronExecutionParseError(`执行契约字段 "mcpServers" 不支持 "${ALL_MCP_SERVERS}"（${kind} 只接受显式点名的连接器数组，` +
            '例如 ["catalyst-juguang"]——脚本经调用桥用的是员工代理身份，必须逐个点名，不支持通配）');
    }
    return optionalStringArray(value, "mcpServers", kind);
}
/**
 * The bridge declaration pair for a script/modelCall, emitted ONLY when actually
 * present on the input.
 *
 * Why conditional (every other optional field in this parser is emitted as an
 * explicit `undefined`): the enterprise V2 control plane re-validates a stored
 * definition with an EXHAUSTIVE key check (`parseEnterpriseTaskServerExecutionV2`
 * → `strictRecord`). Emitting the pair unconditionally would stamp
 * `mcpServers: undefined` / `connectorPolicy: undefined` onto EVERY stored row,
 * including the ones authored before the bridge existed — and a re-parse of such
 * a row would then be judged against keys the row never declared. Emitting the
 * keys only when actually present keeps a declaration-free legacy row's
 * round-trip a zero-diff no-op, which is the whole reason this helper is
 * conditional.
 *
 * The cloud leg DOES have a bridge: `apps/web/server/enterprise/cloud-mcp-bridge.ts`
 * (loopback MCP endpoint started from `apps/web/server/server.ts`, #2739), which a
 * server-fired script reaches via `dosia-mcp call <connector> <tool>`. So a cloud
 * script/modelCall declaring connectors is a promise the runner can keep, and the
 * V2 key sets include both keys — the create-face rules stay the ones in
 * {@link assertBridgeConnectorDeclaration}, not a blanket rejection.
 */
function parseBridgeDeclaration(obj, kind) {
    const mcpServers = optionalBridgeMcpServers(obj?.mcpServers, kind);
    const connectorPolicy = optionalConnectorPolicy(obj?.connectorPolicy, kind);
    return {
        ...(mcpServers !== undefined ? { mcpServers } : {}),
        ...(connectorPolicy !== undefined ? { connectorPolicy } : {}),
    };
}
/**
 * 必须 fail-closed 的连接器集合 —— 点名其中任何一个（或写了显式全量）的任务
 * 都必须声明 `connectorPolicy:"required"`。
 *
 * 判据不是「哪个连接器重要」，而是「连接器缺失时继续跑会不会产出一个看起来
 * 正常、实际拿着不完整数据下的结论」：
 *   · 聚光 = 花钱/投放面，缺数据的日报会被当成真实投放表现读。
 *   · dosia-projects = 项目注册表的写面（绑定账户 / 开采集 / 刷档案），连接器
 *     挂不上时脚本会「没绑上却记成 success」，与聚光同一类假成功。
 *
 * 这是**一份**集合，两个消费点（script/modelCall 的 bridge 门与 agentTurn 的
 * 创建门）共用；再写第二条同构判定就等着两边漂移。
 */
const FAIL_CLOSED_CONNECTOR_NAMES = [
    JUGUANG_MCP_SERVER_NAME,
    DOSIA_PROJECTS_MCP_SERVER_NAME,
];
/** 用户可见文案里念的连接器名（顺序与 FAIL_CLOSED_CONNECTOR_NAMES 一致）。 */
const FAIL_CLOSED_CONNECTOR_LABEL = FAIL_CLOSED_CONNECTOR_NAMES.join(" / ");
/**
 * True when a connector scope names a fail-closed connector (either mount
 * naming form: bare name, or the `<prefix>_<name>` 挂载名) or 显式全量.
 *
 * Exported because the relay MCP facade (`apps/web/server/enterprise/relay-mcp-facade.ts`)
 * decides「要不要替调用方补上 connectorPolicy:"required"」with the SAME predicate —
 * a second hand-rolled copy there is exactly how one side would keep listing only
 * 聚光 while this list grows.
 */
export function declaresFailClosedConnectorOrAll(scope) {
    if (isAllMcpServers(scope))
        return true;
    if (!Array.isArray(scope))
        return false;
    return scope.some((name) => FAIL_CLOSED_CONNECTOR_NAMES.some((connector) => name === connector || name.endsWith(`_${connector}`)));
}
export function declaredConnectorScopeOf(execution) {
    switch (execution.kind) {
        case "systemEvent":
        case "loopWakeup":
            return { declarable: false };
        default:
            return { declarable: true, scope: execution.mcpServers };
    }
}
/**
 * CREATION-face gate for the script/modelCall bridge declaration — called by
 * {@link buildCronExecution} only, never by the bare parse path.
 *
 * Two rules, both「显式说出来」:
 *  1. 声明了连接器就必须声明 `connectorPolicy`. 缺省不是「随便怎样都行」——它是
 *     作者没想过「连接器挂不上时这个任务应该失败还是照跑」。默认成任一边都会造出
 *     一类静默错：默认 optional → 拿不到身份的脚本照跑，产出空报告记成 success
 *     （B1 假成功那一类）；默认 required → 一个只是顺手用一下连接器的脚本会因为
 *     连接器没装而整轮不跑。所以逼作者答一次。
 *  2. 点名 fail-closed 连接器（或写了全量）必须 `"required"` —— 与 agentTurn 先例
 *     同一条规矩：那些连接器缺失时继续跑等于拿着不完整数据下结论。名单单源见
 *     {@link FAIL_CLOSED_CONNECTOR_NAMES}。
 */
export function assertBridgeConnectorDeclaration(execution) {
    if (execution.kind !== "script" && execution.kind !== "modelCall")
        return;
    const servers = execution.mcpServers;
    if (!Array.isArray(servers) || servers.length === 0)
        return;
    if (execution.connectorPolicy === undefined) {
        throw new CronExecutionParseError(`任务声明了连接器（mcpServers: ${JSON.stringify(servers)}）就必须同时声明 connectorPolicy：` +
            '`"required"` = 连接器不可用时本轮直接失败、不跑脚本（推荐，避免拿不到数据还记成成功）；' +
            '`"optional"` = 连接器不可用时照常跑。');
    }
    if (declaresFailClosedConnectorOrAll(servers) && execution.connectorPolicy !== "required") {
        throw new CronExecutionParseError(`声明 ${FAIL_CLOSED_CONNECTOR_LABEL} 连接器的任务必须使用 connectorPolicy:"required"，禁止连接器缺失时继续产出结果`);
    }
}
/**
 * `agentTurn.precheck`（B-124 零模型前置检查），只在输入里**有**这个键时才出键——与
 * {@link parseBridgeDeclaration} 同一个理由：V2 控制面对定义逐键校验（`strictRecord`），无条件写出
 * `precheck: undefined` 会让每一行没写它的存量定义多出一个键。`null` 不是「没写」（JSON 里没有
 * undefined），一律按形状错误拒——要去掉前置检查走 {@link mergeCronExecutionPatch} 的 `precheck: null`。
 * 字段守卫与 `script` 档同一组（command 必填非空、cwd 字符串、timeoutMs 有限数字），未知键剥掉。
 */
function parsePrecheck(obj) {
    if (obj?.precheck === undefined)
        return {};
    const value = obj.precheck;
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw new CronExecutionParseError('执行契约字段 "precheck" 类型错误（agentTurn 的 precheck 须为 {command, cwd?, timeoutMs?} 对象）');
    }
    const raw = value;
    const precheck = { command: requireString(raw?.command, "precheck.command", "agentTurn") };
    const cwd = optionalString(raw?.cwd, "precheck.cwd", "agentTurn");
    if (cwd !== undefined)
        precheck.cwd = cwd;
    const timeoutMs = optionalNumber(raw?.timeoutMs, "precheck.timeoutMs", "agentTurn");
    if (timeoutMs !== undefined)
        precheck.timeoutMs = timeoutMs;
    return { precheck };
}
/**
 * N-174 收件人名单字段。读取面只判形状（缺省 = 不出现；存量行没有这个字段，照常再水化）；「会发消息就必须带」
 * 是写入面的规则（{@link assertMessageRecipientsDeclared}），不在这里。
 */
function parseMessageRecipientsField(obj, kind) {
    const read = readMessageRecipients(obj?.messageRecipients);
    if (read.error)
        throw new CronExecutionParseError(`执行契约字段错误（${kind}）：${read.error}`);
    return read.recipients ? { messageRecipients: read.recipients } : {};
}
/** Guard + allowlist-filter a `script` env map. Non-allowlisted keys are rejected. */
function guardScriptEnv(value, kind) {
    if (value === undefined)
        return undefined;
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw new CronExecutionParseError(`执行契约字段 "env" 类型错误（${kind} 的 env 须为键值对象）`);
    }
    const out = {};
    for (const [k, v] of Object.entries(value)) {
        if (!SCRIPT_ENV_ALLOWLIST.includes(k)) {
            throw new CronExecutionParseError(`执行契约 env 键 "${k}" 不在允许名单内（凭证走宿主注入通道，不写进任务定义）`);
        }
        if (typeof v !== "string") {
            throw new CronExecutionParseError(`执行契约 env 值类型错误（键 "${k}" 的值须为字符串）`);
        }
        out[k] = v;
    }
    return out;
}
/**
 * Parse an unknown value (typically a `JSON.parse` result from a persisted cron
 * job / enterprise task row) into a validated `CronExecution`. Throws a friendly
 * `CronExecutionParseError` — never a raw undefined-access — on any structural
 * problem. Back-compat: existing `systemEvent` / `agentTurn` / `loopWakeup`
 * payloads (without the new optional fields) parse unchanged.
 *
 * Optional `opts` enables path-boundary checks for script/modelCall (authoring
 * gate). Omitted → identical behaviour to the pre-path-gate parser (stock row
 * re-hydration never rejects a historical out-of-tree path).
 */
export function parseCronExecution(value, opts) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw new CronExecutionParseError("执行契约解析失败：期望一个对象，收到 " +
            (value === null ? "null" : Array.isArray(value) ? "数组" : typeof value));
    }
    // Defensive: guard the parent before indexing (红线 #9 `x?.[k]?.y` 姿势).
    const obj = value;
    const kind = obj?.kind;
    if (typeof kind !== "string") {
        throw new CronExecutionParseError('执行契约解析失败：缺少字符串 "kind" 判别字段');
    }
    if (!CRON_EXECUTION_KINDS.includes(kind)) {
        throw new CronExecutionParseError(`执行契约解析失败：未知的 kind "${kind}"（支持：${CRON_EXECUTION_KINDS.join(" / ")}）`);
    }
    let parsed;
    switch (kind) {
        case "systemEvent":
            parsed = {
                kind: "systemEvent",
                targetChatId: requireString(obj?.targetChatId, "targetChatId", "systemEvent"),
                message: requireString(obj?.message, "message", "systemEvent"),
            };
            break;
        case "agentTurn":
            parsed = {
                kind: "agentTurn",
                prompt: requireString(obj?.prompt, "prompt", "agentTurn"),
                model: optionalString(obj?.model, "model", "agentTurn"),
                framework: optionalAgentFramework(obj?.framework),
                agent: optionalString(obj?.agent, "agent", "agentTurn"),
                agentPolicy: optionalAgentPolicy(obj?.agentPolicy),
                connectorPolicy: optionalConnectorPolicy(obj?.connectorPolicy, "agentTurn"),
                timeoutSeconds: optionalNumber(obj?.timeoutSeconds, "timeoutSeconds", "agentTurn"),
                mcpServers: optionalMcpServerScope(obj?.mcpServers, "mcpServers", "agentTurn"),
                // Sibling app-tool lever to mcpServers (~28 dosia-tools). Dropping it here
                // silently re-widened a scheduled Lean/consolidation turn back to the whole
                // tool surface (Bug 1, 红线 #9 单一真相源). `undefined` = legacy whole surface;
                // `[]` = cut all app tools (SDK-native file ops stay).
                dosiaTools: optionalStringArray(obj?.dosiaTools, "dosiaTools", "agentTurn"),
                // Optional at the parse boundary (desktop cron/loop agentTurn has no budget);
                // the SERVER-owned create gate (`assertServerAgentTurnExecution`) is what
                // makes it mandatory for enterprise tasks. Keeping it optional here means an
                // existing desktop agentTurn row round-trips unchanged (红线 #8 无回退分支).
                maxBudgetUsd: optionalNumber(obj?.maxBudgetUsd, "maxBudgetUsd", "agentTurn"),
                // One-task-one-project. Dropping this here would silently unmount L3
                // for a scheduled turn that declared a project (same class as the
                // dosiaTools strip). Omitted = no project archive on the cloud path.
                projectSlug: optionalString(obj?.projectSlug, "projectSlug", "agentTurn"),
                // B-124 零模型前置检查。漏挑进来 = 静默剥掉（全字段往返测试钉住）。
                ...parsePrecheck(obj),
                // N-174 收件人名单。漏挑进来 = 名单被静默剥掉、运行时不再拦名单外的人。
                ...parseMessageRecipientsField(obj, "agentTurn"),
            };
            break;
        case "loopWakeup":
            parsed = {
                kind: "loopWakeup",
                prompt: requireString(obj?.prompt, "prompt", "loopWakeup"),
                model: optionalString(obj?.model, "model", "loopWakeup"),
                agent: optionalString(obj?.agent, "agent", "loopWakeup"),
            };
            break;
        case "script":
            parsed = {
                kind: "script",
                command: requireString(obj?.command, "command", "script"),
                cwd: optionalString(obj?.cwd, "cwd", "script"),
                timeoutMs: optionalNumber(obj?.timeoutMs, "timeoutMs", "script"),
                env: guardScriptEnv(obj?.env, "script"),
                // A2′ bridge declaration. Permissive here on purpose (B1 先例: 创建面严 /
                // 读取面宽) — every stored row predates these fields and must re-hydrate
                // untouched. The `connectorPolicy` requirement lives in the CREATION face
                // (assertBridgeConnectorDeclaration via buildCronExecution).
                ...parseBridgeDeclaration(obj, "script"),
                ...parseMessageRecipientsField(obj, "script"),
            };
            break;
        case "modelCall":
            parsed = {
                kind: "modelCall",
                command: optionalString(obj?.command, "command", "modelCall"),
                systemPrompt: requireString(obj?.systemPrompt, "systemPrompt", "modelCall"),
                promptTemplate: requireString(obj?.promptTemplate, "promptTemplate", "modelCall"),
                model: optionalString(obj?.model, "model", "modelCall"),
                maxBudgetUsd: optionalNumber(obj?.maxBudgetUsd, "maxBudgetUsd", "modelCall"),
                // Same A2′ bridge declaration as the script branch (the pre-fetch command
                // is the consumer); same permissive read face.
                ...parseBridgeDeclaration(obj, "modelCall"),
                ...parseMessageRecipientsField(obj, "modelCall"),
            };
            break;
    }
    assertExecutionPathsInRoots(parsed, opts);
    return parsed;
}
/**
 * The ONLY sanctioned constructor for a `CronExecution`. Every producer (cron_manage,
 * cron-flow skill, enterprise task authoring, server runtime) builds through here
 * rather than hand-rolling an object literal — guaranteeing normalization (unknown
 * keys stripped, script env allowlist enforced) at a single choke point. Reuses the
 * same field guards as `parseCronExecution`, so build-time and parse-time validity
 * can never drift. Throws `CronExecutionParseError` on an invalid input.
 *
 * Optional `opts` turns on path-boundary checks for script/modelCall (see
 * {@link CronExecutionPathOpts}). Stock callers that omit it are unchanged.
 *
 * The ONE rule that lives here and not in `parseCronExecution` is the A2′ bridge
 * declaration gate ({@link assertBridgeConnectorDeclaration}): a stored row must
 * always re-hydrate, but a NEW declaration has to answer「连接器挂不上时怎么办」
 * out loud. Safe for the fire-time rebuild callers (server task-runner /
 * cron-script L2 migration re-`build` an existing execution): a row can only carry
 * `mcpServers` if it came through this gate, so it already satisfies it.
 */
export function buildCronExecution(input, opts) {
    // Route through the parse guard: it strips extras, enforces the env allowlist and
    // rejects malformed input identically to the deserialization boundary — one path.
    const parsed = parseCronExecution(input, opts);
    assertBridgeConnectorDeclaration(parsed);
    return parsed;
}
/**
 * AUTHORING-ONLY gate — reject a `script` / `modelCall` command whose FINAL
 * top-level segment discards the business process's exit status, so the executor
 * can only ever observe `0`.
 *
 * Deliberately NOT inside `buildCronExecution`/`parseCronExecution`: both also run
 * on ALREADY-STORED rows (task-store re-hydration; the server task-runner rebuilds
 * the execution with a resolved cwd on EVERY fire — apps/web task-runner.ts). A
 * legacy job that was authored with a swallowing shape must keep firing (its defect
 * is misreported status, strictly better than a hard red every round) — only NEW
 * writes get rejected. Call sites are therefore the authoring choke points:
 * desktop `cron_manage` create/update (cron-tool.ts), the desktop UI's
 * `CRON_UPSERT` IPC create/update (apps/desktop/src/main/ipc-handlers/cron.ts),
 * `cloud_task_manage` (cloud-task-tool.ts), the enterprise HTTP create/update and
 * the approval promote face (task-actions.ts / review-actions.ts, both through
 * {@link buildServerTaskExecution} / {@link buildNewEnterpriseTaskExecution}), and the
 * recommended-schedule template materializer.
 *
 * Why this is a product gate and not a docs note (2026-08-25, zhangxiaojie 财务链):
 * a job scheduled as `python3 -u m3_m4_report.py --push > m3_run.out 2>&1; echo R=$?`
 * ran with `lastStatus=success` while `lastResult=R=1` — the python process exited 1,
 * but `spawnCollect` runs `shell:true` so the CHAIN's status is `echo`'s `0`, and
 * `runScriptExecution`'s `r.code === 0` branch recorded a clean run. The executor is
 * correct; the shape is the defect, and it is invisible to the author until a report
 * silently goes stale. One gate at the sole authoring choke point covers every
 * producer (cron_manage / cron-flow / enterprise task authoring / recommended-schedule).
 *
 * Deliberately NARROW — a false reject blocks legitimate task creation:
 *  - only the LAST top-level segment is judged (an earlier `echo` is irrelevant);
 *  - `&&` chains are untouched (a failing head short-circuits and its status wins);
 *  - `exit …` is EXPLICITLY allowed — `rc=$?; …; exit "$rc"` is the recommended fix;
 *  - quotes / escapes / `$( )` / backticks are skipped, so a `;` or `||` inside a
 *    string or substitution never counts as a separator.
 */
export function assertExitCodePropagatingCommand(execution) {
    // B-124：前置检查同样按退出码判（0 有活 / 10 没活 / 其它失败），吞掉就永远是「有活」。
    const isPrecheck = execution.kind === "agentTurn";
    const command = execution.kind === "script" ? execution.command
        : execution.kind === "modelCall" ? execution.command
            : execution.kind === "agentTurn" ? execution.precheck?.command
                : undefined;
    if (typeof command !== "string" || command.trim().length === 0)
        return;
    const tail = lastTopLevelSegment(command);
    if (!tail)
        return;
    const shown = exitCodeSwallowingHead(tail.text);
    if (!shown)
        return;
    throw new CronExecutionParseError(`${isPrecheck ? "前置检查" : "执行"}命令的最后一段「${shown}」会吞掉前面业务进程的退出码，` +
        (isPrecheck
            ? `调度器只会看到 0，「没有新活」（退出码 ${CRON_PRECHECK_NO_WORK_EXIT_CODE}）会被当成有活、每一轮都唤醒 AI。`
            : "调度器只会看到 0，失败的任务会被记成成功。") +
        (tail.separator === "||"
            ? "请删掉结尾的 `|| true` / `|| echo …`；需要记录失败时用 `rc=$?` 存下来再 `exit \"$rc\"`。"
            : "请让业务命令作为最后一条命令；确实要记录退出码时改写成 " +
                "`cmd > out 2>&1` / `rc=$?` / `printf 'R=%s\\n' \"$rc\" >> out` / `exit \"$rc\"` 四行。"));
}
/**
 * AUTHORING-ONLY gate — `agentTurn` / `modelCall` 写了单次花费上限（`maxBudgetUsd`）就必须是大于 0 的美元数
 * （判据单源 {@link isValidMaxBudgetUsd}）。0 或负数交到 Claude 引擎手里，CLI 一启动就退出、一次请求都发不出去
 * （周审 2026-10-15 后续项 13）；`parseCronExecution` 的字段 guard 只判「有限数字」，所以这一条单独立在写入面。
 *
 * 与 {@link assertExitCodePropagatingCommand} 同一批写入口、同一个理由不进 build / parse：存量行每次开火都会被
 * 重建（server task-runner）或再水化（task-store 读整份任务列表），读取面收严会让一行脏数据拖垮整份任务列表。
 * 存量里漏网的非正值由交给 SDK 的边界（`toSdkMaxBudgetUsd`）在起进程之前明确失败，不静默放开上限。
 */
export function assertPositiveMaxBudgetUsd(execution) {
    if (execution.kind !== "agentTurn" && execution.kind !== "modelCall")
        return;
    const { maxBudgetUsd } = execution;
    if (maxBudgetUsd === undefined || isValidMaxBudgetUsd(maxBudgetUsd))
        return;
    throw new CronExecutionParseError(invalidMaxBudgetUsdMessage(maxBudgetUsd));
}
/**
 * AUTHORING-ONLY gate（N-174 §4.2）——命令或提示词里有发消息工具（shared `detectMessageSendTools`）的执行配置，
 * 必须带收件人名单与一条样例消息（`messageRecipients`），否则不写库。
 *
 * 与 {@link assertExitCodePropagatingCommand} 同一批写入口、同一个理由不进 build / parse：存量任务每次开火都会被重建 /
 * 再水化，读取面收严会让没带名单的存量任务停跑——维护者 2026-10-26 拍板 D2=A「存量照跑，下次修改时要求补名单」。
 * 新建面（cron_manage add、cron:upsert 新建、云端 buildNewEnterpriseTaskExecution）直接调本函数；修改面调
 * {@link assertMessageRecipientsDeclaredOnUpdate}（改到命令 / 提示词才判）。
 *
 * `extraTexts`：执行配置之外、同样会被执行的文本（企业任务设备腿 agentTurn 执行的是任务顶层 `prompt`）。
 */
export function assertMessageRecipientsDeclared(execution, extraTexts = []) {
    const reason = missingMessageRecipientsReason(execution, extraTexts);
    if (reason)
        throw new CronExecutionParseError(reason);
}
/**
 * 修改面的同一道门：只在这次修改改到了会被执行的命令 / 提示词时判（`messageSendTextsChanged`）——AI 把日报改成「私聊
 * 3 位同事」这种修改一定改到提示词，会被要求补名单；只换模型的修改（任务详情「更换模型」）不判，存量任务不会因此改不动。
 * `before` 缺省（读不到旧配置）按新建判。
 */
export function assertMessageRecipientsDeclaredOnUpdate(before, after) {
    if (before && !messageSendTextsChanged(before, after))
        return;
    assertMessageRecipientsDeclared(after);
}
/** Commands that always succeed, so ending a chain with one pins the status to 0. */
const EXIT_CODE_SWALLOWING_HEADS = new Set(["echo", "printf", "true", ":"]);
/**
 * Split on top-level `;` / newline / `||` and return the final segment together with
 * the operator that introduced it. Returns null when the command is a single segment
 * (nothing can be swallowing anything) or when it ends with an operator.
 *
 * `&&` is scanned so it is not mistaken for `|`-family operators, but never splits:
 * `a && b` fails as `a` when `a` fails, so it propagates correctly.
 */
function lastTopLevelSegment(command) {
    let current = "";
    let separator = "";
    let lastSeparator = "";
    let quote = null;
    let depth = 0; // $( … ) nesting
    let backtick = false; // ` … ` (cannot nest)
    const segments = [];
    const push = () => {
        segments.push({ text: current, separator });
        current = "";
        separator = lastSeparator;
    };
    for (let i = 0; i < command.length; i += 1) {
        const ch = command[i];
        if (quote === "'") {
            current += ch;
            if (ch === "'")
                quote = null;
            continue;
        }
        // 单引号段已在上面 continue 掉了（shell 里单引号内没有转义），所以走到这里
        // 一定是无引号或双引号上下文——两者的 `\` 都是转义前缀。
        if (ch === "\\") {
            current += ch + (command[i + 1] ?? "");
            i += 1;
            continue;
        }
        if (quote === '"') {
            current += ch;
            if (ch === '"')
                quote = null;
            continue;
        }
        if (ch === "'" || ch === '"') {
            quote = ch;
            current += ch;
            continue;
        }
        if (ch === "$" && command[i + 1] === "(") {
            depth += 1;
            current += "$(";
            i += 1;
            continue;
        }
        if (ch === "`") {
            backtick = !backtick;
            current += ch;
            continue;
        }
        if (ch === ")" && depth > 0) {
            depth -= 1;
            current += ch;
            continue;
        }
        if (depth > 0 || backtick) {
            current += ch;
            continue;
        }
        if (ch === "|" && command[i + 1] === "|") {
            lastSeparator = "||";
            push();
            i += 1;
            continue;
        }
        if (ch === "&" && command[i + 1] === "&") {
            // Not a split point, but consume both chars so the second `&` cannot be
            // misread and so a following `|` is judged on its own.
            current += "&&";
            i += 1;
            continue;
        }
        if (ch === ";") {
            lastSeparator = ";";
            push();
            continue;
        }
        if (ch === "\n") {
            lastSeparator = "\n";
            push();
            continue;
        }
        current += ch;
    }
    push();
    // A trailing separator (`cmd; echo x;`) leaves an empty final segment — walk back
    // to the last segment that actually carries a command, otherwise the shape would
    // slip through on a stray trailing `;` or newline.
    for (let i = segments.length - 1; i >= 0; i -= 1) {
        const segment = segments[i];
        if (!segment || segment.text.trim().length === 0)
            continue;
        return segment.separator === "" ? null : segment;
    }
    return null;
}
/** The swallowing command at the head of `segment`, or null when it propagates. */
function exitCodeSwallowingHead(segment) {
    const text = segment.trim();
    if (!text)
        return null;
    // `:` is a complete command by itself; any other head ends at whitespace or at a
    // redirection glued straight onto it (`echo>out`).
    const head = text === ":" ? ":" : (text.split(/[\s<>|&]/, 1)[0] ?? "");
    if (!EXIT_CODE_SWALLOWING_HEADS.has(head))
        return null;
    return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}
/**
 * 「定时任务被拒」写入口报错（B-203 R10）。`field` = 命中字段的路径（`execution.command`、`params.webhook`）。
 * 本函数即逐字终稿（行为口径见 docs/system/design/scheduled-tasks.md「任务里不放密钥（B-203）」）；改字同步改测试里的逐字断言。
 */
export function plaintextSecretInTaskMessage(field) {
    return (`任务没有创建：${field} 里有密码、密钥或令牌。定时任务的设置会存进任务记录（云端任务存在公司后台），密钥不能写在这里。\n` +
        "发群消息请改用已授权的钉钉 / 飞书连接器；投放平台、飞书、钉钉等请改用对应连接器；其它密钥暂时不要放进定时任务。");
}
/**
 * AUTHORING-ONLY gate（B-203 R10 / R11）— 执行配置（命令、提示词、提醒内容……每个字符串叶子）或任务参数
 * `params` 里有识别出的密码 / 密钥 / 令牌，就拒绝写入，报 {@link plaintextSecretInTaskMessage}，指出第一处
 * 命中的字段路径。判据只有一份：{@link findSecretSpans}（secret-shapes.json，与进模型前打码、上传会话记录前
 * 打码、写文件门同一张表）。
 *
 * 业务白话：员工让 AI 建「每天用 root 密码登服务器拉数」「往群机器人地址发日报」这类任务时，密钥会原样存进
 * 任务记录（云端任务存在企业后端），每次开火还会被拼进提示词。这里在写入那一刻拒掉，并说明改走连接器。
 *
 * 只在写入口跑，不进 {@link parseCronExecution} / {@link buildCronExecution}：与
 * {@link assertExitCodePropagatingCommand} 同律——存量任务每次开火都会被重建（server task-runner、本机 task-store
 * 再水化），读取面必须永远能读回；已经带着密钥的存量任务照常开火（R11），只拦新的写入。调用点与吞码护栏
 * 同一位置：本机 `cron_manage` add / update（cron-tool.ts）、任务页 IPC `cron:upsert`
 * （apps/desktop/src/main/ipc-handlers/cron.ts）、值守循环 `start_loop`（loop-tool.ts，判 `loop.goal` /
 * `loop.stopCondition`）、企业云端任务三个写入口经 {@link buildServerTaskExecution} /
 * {@link buildNewEnterpriseTaskExecution}（云端任务没有 params；顶层 `prompt` 在创建 / 晋升面由后者判、
 * 更新面由 `parseUpdateTaskPayload` 判——不带执行配置的老式任务，员工电脑执行的就是它）。
 *
 * 字段路径：执行配置以 `extras.executionField` 起头（缺省 `execution`；企业设备腿传 `deviceExecution`），
 * params 以 `params` 起头，`extras.fields` 里的其它随任务存下的字段以各自的键起头（`loop.goal`、`prompt`）；
 * 报错顺序 = 执行配置 → params → fields。对象键用 `.`、数组下标用 `[i]`。对象里的字符串值另带键名判一次
 * （`"password":"…"`）：本机运行时把 params 原样 JSON 化拼进提示词（core runtime.ts `[params] ${JSON.stringify(job.params)}`），
 * 键名就是那段文字的上下文——只看值认不出 `{"password":"Root@2026"}`。
 *
 * `execution` 可为 undefined（update 只改 params 时）。抛 `CronExecutionParseError`，写入口沿用各自既有的
 * 拒绝映射（cron_manage 工具错误、IPC 拒绝、web 400）。
 */
export function assertNoPlaintextSecretInAuthoring(execution, extras) {
    let hit = (execution !== undefined ? firstSecretFieldPath(execution, extras?.executionField ?? "execution", undefined, 0) : null) ??
        (extras?.params !== undefined ? firstSecretFieldPath(extras.params, "params", undefined, 0) : null);
    for (const [field, value] of Object.entries(extras?.fields ?? {})) {
        if (hit !== null)
            break;
        hit = firstSecretFieldPath(value, field, undefined, 0);
    }
    if (hit !== null)
        throw new CronExecutionParseError(plaintextSecretInTaskMessage(hit));
}
/** 深度上限：与 redactSecretsDeep 同值；更深的结构不再下钻（params 是模型 / 员工手写的小对象）。 */
const SECRET_SCAN_MAX_DEPTH = 64;
/** 第一处含密钥的字符串叶子的字段路径；没有则 null。`key` = 该叶子在父对象里的键名（数组元素没有）。 */
function firstSecretFieldPath(value, path, key, depth) {
    if (typeof value === "string") {
        if (findSecretSpans(value).length > 0)
            return path;
        if (key !== undefined && findSecretSpans(`${JSON.stringify(key)}:${JSON.stringify(value)}`).length > 0)
            return path;
        return null;
    }
    if (depth >= SECRET_SCAN_MAX_DEPTH || value === null || typeof value !== "object")
        return null;
    if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i += 1) {
            const hit = firstSecretFieldPath(value[i], `${path}[${i}]`, undefined, depth + 1);
            if (hit !== null)
                return hit;
        }
        return null;
    }
    for (const [k, v] of Object.entries(value)) {
        const hit = firstSecretFieldPath(v, `${path}.${k}`, k, depth + 1);
        if (hit !== null)
            return hit;
    }
    return null;
}
/**
 * SERVER-context creation gate for an `agentTurn` execution (spec §1). An
 * enterprise task whose `execution.kind === "agentTurn"` is SERVER-owned (the
 * partition predicate is `execution != null`), so at CREATE time it must declare:
 *   - `mcpServers` — the tool surface. THREE legal answers, all EXPLICIT:
 *       `[]`      仅内置工具
 *       `[...]`   点名的连接器子集
 *       `"all"`   全部已授权连接器（{@link ALL_MCP_SERVERS}）
 *     `undefined` stays REJECTED — the point of this gate was never "forbid the
 *     full surface", it was "forbid inheriting the full surface by ACCIDENT". A
 *     model that forgot the field and a model that means everything must not land
 *     on the same value (2026-07-29「聚光时报」: with `"all"` unavailable, the only
 *     legal answer to "I need connectors I can't enumerate" was `[]` — three weeks
 *     of empty daily reports, no error anywhere). 全量 is now sayable, and being
 *     sayable is what makes 遗漏 safely rejectable.
 *
 *     Scope disclosure moved from "narrow the surface at authoring time" to
 *     "enumerate the surface at approval time": the cloud-task approval flow shows
 *     an admin the resolved 能力范围 card before the task can ever fire, and the
 *     run record reports what actually mounted (`mountedMcpServers`).
 *   - `framework` — `"claude"` or `"pi"`, explicit (the `cloud_task_manage` tool fills
 *     the default before submit; the HTTP create path does not).
 *   Conditional rules (see the function body): `agentPolicy: "required"` when an
 *   `agent` is named; `connectorPolicy` for fail-closed connectors; pi rejects
 *   `maxBudgetUsd`.
 *
 * `maxBudgetUsd` is NO LONGER required (task-cost-observe 2026-07-15): the
 * AI-estimated per-run budget fuse silently halted legitimate cloud tasks
 * mid-run (a scheduled 聚光日报 hitting its guessed $0.8 cap before the report /
 * attachment finished), so the single-run cap was RETIRED as a risk source (红线
 * #7 删风险源不加护栏). Cloud-task cost is now governed by observation
 * (usage accounting) + admin overage alert (cloud-task-budget), not a hard
 * per-run封顶. `maxBudgetUsd` survives as an OPTIONAL field an admin can still set
 * explicitly, but the AI never has to fill it and its absence is legal — matching
 * the desktop cron/loop agentTurn path, which never gated on budget.
 *
 * This is the SINGLE source of the mcpServers rule for the enterprise-task
 * producers (task-actions `createTask` HTTP path + `cloud_task_manage` tool),
 * reached on every enterprise write face — create, update AND approval promote —
 * through {@link buildServerTaskExecution} (B-128: the promote face used to skip it). It
 * is DELIBERATELY separate from `parseCronExecution` so the shared parser stays
 * permissive for the desktop cron/loop agentTurn path — the gate is a
 * server-context assertion, not a universal `CronExecution` invariant. No-op for
 * non-agentTurn kinds. Throws a friendly `CronExecutionParseError` (host-name-free)
 * that the producers surface as a 400 / tool error.
 *
 * Mirrors the wire-time enforcement in `buildAgentHostTurnRequest` (agent-host
 * contract): a value that passes THIS create gate always satisfies that builder,
 * so the two boundaries can never drift.
 */
export function assertServerAgentTurnExecution(execution) {
    if (execution.kind !== "agentTurn")
        return;
    if (execution.framework === undefined) {
        throw new CronExecutionParseError('新建或更新云端完整 AI 任务（agentTurn）必须显式声明 framework（"claude" 或 "pi"）');
    }
    if (!Array.isArray(execution.mcpServers) && !isAllMcpServers(execution.mcpServers)) {
        throw new CronExecutionParseError("云端完整 AI 任务（agentTurn）必须声明工具面 mcpServers（空数组 [] = 仅用内置工具；" +
            `点名数组 = 只挂这几个连接器；"${ALL_MCP_SERVERS}" = 全部已授权连接器）`);
    }
    if (execution.agent && execution.agentPolicy !== "required") {
        throw new CronExecutionParseError('云端任务声明 agent 时必须同时声明 agentPolicy:"required"，禁止解析失败后退回默认人格');
    }
    if (!execution.agent && execution.agentPolicy === "required") {
        throw new CronExecutionParseError('agentPolicy:"required" 必须同时声明 agent');
    }
    if (execution.agent && execution.model?.trim()) {
        throw new CronExecutionParseError(AGENT_WITH_MODEL_REJECTED_MESSAGE);
    }
    if (declaresFailClosedConnectorOrAll(execution.mcpServers) && execution.connectorPolicy !== "required") {
        throw new CronExecutionParseError(`${FAIL_CLOSED_CONNECTOR_LABEL} 或显式全量连接器任务必须声明 connectorPolicy:"required"，禁止连接器缺失时继续生成结果`);
    }
    if (execution.framework === "pi" && execution.maxBudgetUsd !== undefined) {
        throw new CronExecutionParseError(PI_MAX_BUDGET_UNSUPPORTED_MESSAGE);
    }
}
/**
 * 企业云端任务**服务器腿**（`execution`）的创建期规则——唯一一份清单（B-128）：解析 + 桥声明门
 * （{@link buildCronExecution} 内的 {@link assertBridgeConnectorDeclaration}）→ 吞退出码护栏
 * （{@link assertExitCodePropagatingCommand}）→ 明文密钥护栏（{@link assertNoPlaintextSecretInAuthoring}，B-203）
 * → 完整 AI 任务创建门（{@link assertServerAgentTurnExecution}）→ 单次花费上限必须为正（{@link assertPositiveMaxBudgetUsd}）。
 *
 * 写入口有三处，都只调这一份：HTTP 创建面与审批晋升面经 {@link buildNewEnterpriseTaskExecution}，
 * 更新面（`apps/web/server/enterprise/task-actions.ts` `parseUpdateTaskPayload`，只能改服务器腿）直接调本函数。
 * 以前三处各自手抄这三道门，晋升面只抄了前两道——完整 AI 任务专属的规则（显式 framework、显式
 * mcpServers、指名 Agent 必须 required、指名 Agent 不许再写 model（N-182 D10）、点名聚光 / 项目连接器或要全量时
 * 必须 connectorPolicy:"required"、Pi 不许 maxBudgetUsd）在批准时一条都没过，规则在提案排队期间收紧过，旧提案一批准就绕过去了。
 *
 * 只在写入口跑，不进 {@link parseCronExecution} / {@link buildCronExecution}：存量任务每次开火都会被
 * 重建（server task-runner），读取面必须永远能再水化，理由见 {@link assertExitCodePropagatingCommand}。
 * 抛 `CronExecutionParseError`，写入口映射成 400。
 */
export function buildServerTaskExecution(input) {
    const execution = buildCronExecution(input);
    assertExitCodePropagatingCommand(execution);
    assertNoPlaintextSecretInAuthoring(execution);
    assertServerAgentTurnExecution(execution);
    assertPositiveMaxBudgetUsd(execution);
    return execution;
}
/**
 * **新建**一条企业云端任务时，载荷里执行配置的创建期规则——唯一一份清单（B-128）。
 *
 * 新建任务有两个入口，两个都是创建面，都只调本函数：
 *   · HTTP 创建面 `parseCreateTaskPayload`（apps/web/server/enterprise/task-actions.ts），传原始 body；
 *   · 审批晋升面 `promoteTaskSubmission`（同目录 review-actions.ts）——「批准」= 替提交人新建一条
 *     线上任务，传 `parsePendingTaskSubmission` 解析出的载体（载体解析是读取面，存量提案必须能
 *     再水化，规则不在那里）。已解析的执行配置再解析一次结果不变。
 * 以后加一道创建门，加在这里，两个入口同时生效；不要在某个入口旁边再手抄一条。
 *
 * 依次判（顺序就是报错优先级，与 B-128 之前的 HTTP 创建面逐条相同）：
 *   1. 「在哪儿跑」只能有一个答案（{@link assertExclusiveExecutionPlacement}）——先答这个更根本的问题，
 *      文案才不会被字段的结构错误盖掉；
 *   2. 周报送达标记只能配服务器腿（{@link assertResultDeliveryPlacement}）；
 *   3. 事件触发来源只能由企业后端自己建（{@link assertNoEventSourceInPayload}；晋升载体解析不拷这个字段，
 *      在晋升面恒为空过）；
 *   4. 服务器腿：{@link buildServerTaskExecution}；
 *   5. 设备腿：解析（用 parse 不用 build：设备腿结构上不许带桥，下一步就拒，桥声明门在这条腿上
 *      永远空跑）→ {@link assertDeviceExecution} → 设备 agentTurn 写入约束（N-106，
 *      {@link assertDeviceAgentTurnAuthoring}：时限上限、pi 不收花费上限等）→ 吞退出码护栏
 *      （设备腿的 script 同样按退出码判成功）→ 单次花费上限必须为正（{@link assertPositiveMaxBudgetUsd}）
 *      → 明文密钥护栏（{@link assertNoPlaintextSecretInAuthoring}，字段路径从 `deviceExecution` 起头）；
 *   6. 顶层 `prompt` 的明文密钥护栏（字段路径 `prompt`）：两条腿都不填的老式任务、以及设备腿 agentTurn，
 *      员工电脑执行的都是这段任务描述（cloud-task-tool.ts `DEVICE_EXECUTION_SCHEMA_DESC`），它同样存在企业后端。
 *      不是字符串时不判（结构校验归调用方：创建面 `parseCreateTaskPayload` 已先要求非空字符串，晋升载体同）。
 *   7. 「飞书群来新消息时跑」声明（{@link assertFeishuTriggerPlacement}，N-163 P3）：放在两条腿都建好之后，
 *      要看服务器腿解析后的 `kind`。
 *
 * 云端任务没有 `params`（`CreateEnterpriseTaskInput` 无此字段）。
 *
 * 不在这里的写入口规则（判的不是载荷本身，各入口自己判）：项目权限门判的是「操作人」能不能绑这个
 * 项目、要读项目名册；指名 Agent 的云端归属闸（N-30）要读企业文件库。抛 `CronExecutionParseError`。
 */
export function buildNewEnterpriseTaskExecution(input) {
    assertExclusiveExecutionPlacement(input);
    assertResultDeliveryPlacement(input);
    assertNoEventSourceInPayload(input);
    const built = {};
    if (input.execution !== undefined) {
        built.execution = buildServerTaskExecution(input.execution);
        // N-174：会替员工发消息的任务必须带收件人名单（审批卡上列给管理员看）。修改面另有「改到执行内容才判」的同一道门
        // （assertMessageRecipientsDeclaredOnUpdate，task-actions updateTask），所以不放进 buildServerTaskExecution。
        assertMessageRecipientsDeclared(built.execution);
    }
    if (input.deviceExecution !== undefined) {
        const deviceExecution = parseCronExecution(input.deviceExecution);
        assertDeviceExecution(deviceExecution);
        // N-106：员工电脑会照着跑的时限 / 引擎 / 花费上限的写入约束。只在创建面判，不进 assertDeviceExecution
        // （后者员工电脑开跑前也会重跑，放进去会让存量任务突然停跑）。
        assertDeviceAgentTurnAuthoring(deviceExecution);
        assertExitCodePropagatingCommand(deviceExecution);
        assertPositiveMaxBudgetUsd(deviceExecution);
        assertNoPlaintextSecretInAuthoring(deviceExecution, { executionField: "deviceExecution" });
        // N-174：员工电脑上的设备腿 agentTurn 执行的是任务顶层 prompt，一并判。
        assertMessageRecipientsDeclared(deviceExecution, deviceExecution.kind === "agentTurn" && typeof input.prompt === "string" ? [input.prompt] : []);
        built.deviceExecution = deviceExecution;
    }
    const feishuTrigger = assertFeishuTriggerPlacement({
        feishuTrigger: input.feishuTrigger,
        execution: built.execution,
        deviceExecution: input.deviceExecution,
        resultDelivery: input.resultDelivery,
    });
    if (feishuTrigger)
        built.feishuTrigger = feishuTrigger;
    if (typeof input.prompt === "string") {
        assertNoPlaintextSecretInAuthoring(undefined, { fields: { prompt: input.prompt } });
    }
    return built;
}
/** Narrowing type guard — true when `value` parses as a valid `CronExecution`. */
export function isCronExecution(value) {
    try {
        parseCronExecution(value);
        return true;
    }
    catch {
        return false;
    }
}
function isPlainExecutionObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function executionPatchKind(value) {
    return isPlainExecutionObject(value) && typeof value.kind === "string" ? value.kind : undefined;
}
/**
 * 本机任务执行配置里「钉的模型」这一项：只有 `agentTurn` / `loopWakeup` / `modelCall` 有（`supported`），没指定 = `model`
 * 为 undefined；定时提醒、脚本没有这一项（定时提醒的会话用「设置 → AI 模型」里的模型）。任务详情的「模型」一行与
 * 「更换模型」、调度器「需要更换模型」通知给不给「换个模型」按钮（N-173 B1=A）都按这一份判。
 */
export function cronExecutionModel(exec) {
    if (exec.kind === "agentTurn" || exec.kind === "loopWakeup" || exec.kind === "modelCall") {
        return { supported: true, model: exec.model?.trim() || undefined };
    }
    return { supported: false };
}
/**
 * update 语义的单一真相源：一份「改执行配置」的补丁怎么落到现有执行配置上。
 *
 * - 补丁与现有执行同 kind，或补丁根本没写 kind → **合并**（现有字段逐字保留，补丁里给了的键覆盖）；
 * - 补丁换了 kind → **整体替换**（旧形态的字段对新形态没有意义）；
 * - 任一侧不是对象 → 原样返回补丁，交给 `buildCronExecution` 拒绝。
 *
 * 为什么必须合并（jianghongyi 2026-10-09 实证）：AI 只想换模型，按惯例补上 prompt，却漏了
 * model / timeoutSeconds / dosiaTools——整段替换把三项一起丢了，任务从此用默认模型、默认超时、
 * 全量工具面在跑。云端任务中继（apps/web relay-mcp-facade `case "update"`）早就是这个语义；
 * 本机 `cron_manage update` 与 `cron:upsert` 的更新路径走同一函数，三处不各写一份。
 *
 * 只做合并、不做校验：返回值必须再过 `buildCronExecution`（调用方的事）。
 *
 * 唯一的「删字段」写法：补丁里 `precheck: null` = 去掉前置检查（B-124）。JSON 表达不了 undefined，
 * 合并语义又让「不写」等于保留，没有这一条就再也关不掉一条任务的前置检查。只认这一个键：
 * 其它字段写 `null` 照旧交给 builder 按形状错误拒（不顺手放宽成通用删除语义）。
 */
export function mergeCronExecutionPatch(current, patch) {
    if (!isPlainExecutionObject(current) || !isPlainExecutionObject(patch))
        return patch;
    const patchKind = executionPatchKind(patch);
    if (patchKind !== undefined && patchKind !== executionPatchKind(current))
        return patch;
    const merged = { ...current, ...patch };
    if (patch.precheck === null)
        delete merged.precheck;
    return merged;
}
//# sourceMappingURL=execution-contract.js.map